import { Fragment } from "react";

import { Divider, Typography } from "@mui/material";

import ErrorState from "@app/analytics/components/ErrorState";
import { NonInclusiveTermsGradientRange } from "@app/analytics/components/GradientRange/CoachingGradientRange";
import TableCell from "@app/analytics/components/Table/TableCell";
import { getNonInclusiveTermsDescription } from "@app/analytics/utils/coachingUtils";
import { HeadCell } from "@app/common/hooks/useDataTable";
import { CoachingMoment, CoachingSession } from "@app/common/types";
import { getNonInclusiveTermsSemanticScore } from "@app/common/utils/coachingUtils";

import { Column, Row } from "@rui/Stack";

import CoachingMoments from "./components/CoachingMoments";
import CoachingTable from "./components/CoachingTable";
import useCoachingDashboardData from "./hooks/useCoachingDashboardData";
import {
  Card,
  CardText,
  EmptyCardText,
  LoadingSkeleton,
  SectionDescription,
  SectionHeading,
  SectionOverviewContainer,
  SectionSubHeading,
  ValueWithSemanticIcon,
} from "./styles";

const MAX_WORDS = 3;

const NON_INCLUSIVE_TERMS_HEAD_CELLS: HeadCell<CoachingSession>[] = [
  {
    id: "count",
    label: "# of Terms",
  },
  {
    id: "words",
    label: "Most common",
    sortBy: (d) => d.words[0]?.displayValue.toLowerCase(),
  },
];

/** Assumes words are already sorted in desc order by count */
const getMomentWords = ({ words }: CoachingMoment) => {
  const wordsToDisplay = words.slice(0, MAX_WORDS);
  const numTruncatedWords = words.length - wordsToDisplay.length;
  return `${wordsToDisplay
    .map(({ displayValue }) => `"${displayValue}"`)
    .join(", ")}${numTruncatedWords ? `, +${numTruncatedWords}` : ""}`;
};

/** Assumes words are already sorted in desc order by count */
const getSessionWords = (words: CoachingSession["words"]) => {
  const wordsToDisplay = words.slice(0, MAX_WORDS);
  return `${wordsToDisplay
    .map(
      ({ displayValue, count }) =>
        `${displayValue}${count > 1 ? ` (${count})` : ""}`
    )
    .join(", ")}`;
};

const NonInclusiveTerms = () => {
  const { isError, isInitialLoading, nonInclusiveTerms, refetch } =
    useCoachingDashboardData();

  const mostCommonWords = nonInclusiveTerms.words.slice(0, 3);
  const termsPerMeeting = nonInclusiveTerms.average;
  const descriptionSegments = getNonInclusiveTermsDescription(termsPerMeeting);

  if (isError) {
    return <ErrorState refetch={refetch} />;
  }

  return (
    <>
      <Column spacing={1}>
        <Row variant="apart">
          <SectionHeading>Non-inclusive terms</SectionHeading>
          {termsPerMeeting != null && (
            <ValueWithSemanticIcon
              score={nonInclusiveTerms.semanticScore}
              variant="h3"
              sx={{ fontWeight: 500 }}
            >
              {termsPerMeeting.formattedValue} per meeting
            </ValueWithSemanticIcon>
          )}
        </Row>
        <SectionDescription>
          {descriptionSegments.map((segment) =>
            segment.style ? (
              <span key={segment.value} style={segment.style}>
                {segment.value}
              </span>
            ) : (
              <Fragment key={segment.value}>{segment.value}</Fragment>
            )
          )}
        </SectionDescription>
        <SectionOverviewContainer spacing={1}>
          <Card title="Range" subtitle="Avg terms / meeting">
            {isInitialLoading ? (
              <LoadingSkeleton width="100%" />
            ) : termsPerMeeting.value == null ? (
              <EmptyCardText />
            ) : (
              <NonInclusiveTermsGradientRange
                count={termsPerMeeting.rawValue}
              />
            )}
          </Card>
          <Card title="Most common">
            {isInitialLoading ? (
              <LoadingSkeleton width="100%" />
            ) : termsPerMeeting.value == null ? (
              <EmptyCardText />
            ) : (
              <CardText>
                {mostCommonWords
                  .map(
                    ({ displayValue, count }) => `${displayValue} (${count})`
                  )
                  .join(", ")}
              </CardText>
            )}
          </Card>
        </SectionOverviewContainer>
      </Column>
      <Divider />
      <Column spacing={1}>
        <SectionSubHeading>Moments</SectionSubHeading>
        <SectionDescription>
          Review moments where you used non-inclusive terms.
        </SectionDescription>
        <CoachingMoments
          getMomentLabel={getMomentWords}
          isLoading={isInitialLoading}
          moments={nonInclusiveTerms.moments}
          type="non_inclusive_terms"
        />
      </Column>
      <Divider />
      <Column spacing={1}>
        <SectionSubHeading>Meetings</SectionSubHeading>
        <SectionDescription>
          Review meeting reports where you used non-inclusive terms.
        </SectionDescription>
        <CoachingTable
          defaultOrderBy="count"
          isLoading={isInitialLoading}
          renderSectionCells={(session) => (
            <>
              <TableCell>
                <ValueWithSemanticIcon
                  score={getNonInclusiveTermsSemanticScore(session.count)}
                  variant="bodySecondaryReg"
                  sx={{ minWidth: "30px", textAlign: "right" }}
                >
                  {session.count}
                </ValueWithSemanticIcon>
              </TableCell>
              <TableCell>
                <Typography
                  sx={{
                    font: "inherit",
                    maxWidth: "200px",
                    whiteSpace: "break-spaces",
                  }}
                >
                  {getSessionWords(session.words)}
                </Typography>
              </TableCell>
            </>
          )}
          rows={nonInclusiveTerms.sessions}
          sectionHeadCells={NON_INCLUSIVE_TERMS_HEAD_CELLS}
        />
      </Column>
    </>
  );
};

export default NonInclusiveTerms;
